/**
 * Modal del DNI del autor: UNO SOLO para las dos cosas que se hacen con la
 * misma foto — completar el formulario con los datos del documento y enviarlo
 * al servidor de SADAIC.
 *
 * Son el mismo trámite visto de dos lados, así que pedir las fotos dos veces
 * sobraba. Se cargan frente y dorso una vez y de ahí sale todo: del dorso, el
 * MRZ con apellido, nombres, número y fecha verificados por sus dígitos de
 * control, más el CUIL y el domicilio impresos arriba; del frente, la
 * comprobación de que la foto es de ESE documento.
 *
 * Todo el OCR corre LOCAL en el browser (tesseract.js WASM): mientras no se
 * toque "Enviar", las fotos no salen del dispositivo.
 *
 * Regla dura para ENVIAR: número, nombre completo y fecha de nacimiento
 * legibles Y coincidentes con los datos del formulario. Si no coinciden porque
 * el formulario está vacío o mal cargado, el propio modal ofrece completarlo
 * con lo leído, y ahí la comprobación se rehace sola.
 *
 * El Nº de Trámite se REDACTA de la imagen porque permite hacer gestiones a
 * nombre del titular: se detecta y se tapa solo, y queda la herramienta para
 * taparlo a mano si se escapó. Eso es una sugerencia y no una condición — el
 * envío se habilita con las dos fotos cargadas y verificadas, sin más—. Lo que
 * se sube es la imagen ya redactada, al server de jupiter, que la entrega al
 * servidor de SADAIC.
 */

import React from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import type { Verificacion } from './reader';
import type { OpcionRef } from './dniExtract';
import { CamaraDialog } from './CamaraDialog';
import { textosDni } from './textos';
import type { IdiomaDni } from './textos';
import { PREVIEW_WIDTH, useLecturaDni } from './useLecturaDni';
import type { AutorDelFormulario, DestinoDni, SideStatus } from './useLecturaDni';

const DEFAULT_LANG = (process.env.REACT_APP_DEFAULT_LANGUAGE || 'es') as string;
const T = textosDni(DEFAULT_LANG);

const GENERO_VISIBLE: Record<string, string> = {
  male: DEFAULT_LANG === 'en' ? 'Male' : 'Masculino',
  female: DEFAULT_LANG === 'en' ? 'Female' : DEFAULT_LANG === 'pt' ? 'Feminino' : 'Femenino',
};

export interface DniUploadDialogProps {
  open: boolean;
  onClose: () => void;
  destino: DestinoDni;
  author: AutorDelFormulario;
  onUploaded: () => void;
  /** Las opciones de los campos de referencia, para resolver el domicilio. */
  lookups?: Record<string, OpcionRef[]>;
  /** Si el formulario YA tiene un domicilio cargado (define el tilde inicial). */
  domicilioCargado?: boolean;
  /**
   * Completa el formulario con lo leído del documento. Sin esto el modal sigue
   * sirviendo para enviar, pero no ofrece completar nada. El segundo argumento
   * es el domicilio SIN repartir, y llega solo cuando no se pudo repartir.
   */
  onExtracted?: (valores: Record<string, any>, domicilioSinRepartir?: string) => void;
}

export const DniUploadDialog: React.FC<DniUploadDialogProps> = ({
  open,
  onClose,
  destino,
  author,
  onUploaded,
  lookups,
  domicilioCargado,
  onExtracted,
}) => {
  const {
    ACCIONABLES,
    aceptarCorte,
    anversoError,
    anversoStatus,
    avisoEnvio,
    camara,
    camposTotales,
    canSend,
    completado,
    completar,
    copiarSiDifiere,
    cuitDelDocumento,
    datos,
    domicilio,
    enviado,
    estaVerificado,
    extrayendo,
    faltanDelFrente,
    handleFoto,
    handleSend,
    incluirDomicilio,
    marcas,
    nacionalidadConfirmada,
    nombreDeFila,
    onPreviewDown,
    onPreviewUp,
    previewAnversoRef,
    previewReversoRef,
    propuesta,
    propuestaRef,
    reasonMsg,
    rechazarCorte,
    renglonesAnverso,
    reversoError,
    reversoStatus,
    revisionCuit,
    sendError,
    sending,
    setCamara,
    setIncluirDomicilio,
    setTapadoAutomatico,
    sexoConfirmado,
    tapadoAutomatico,
    tramiteTapadoFrente,
    tapadoDorso,
    tapadoFrente,
    textoDe,
    vencido,
    vencimiento,
    verdictAnverso,
    verdictDorso,
    vieneAlCaso,
    zonasSensiblesAnverso,
  } = useLecturaDni({
    open,
    destino,
    author,
    onUploaded,
    lookups,
    domicilioCargado,
    onExtracted,
    idioma: DEFAULT_LANG as IdiomaDni,
  });

  const botonesDeFoto = (lado: 'anverso' | 'reverso', onFile: (f: File) => void, status: SideStatus) => (
    <>
      <Button variant="outlined" component="label" disabled={status === 'processing'}>
        {T.elegir}
        <input
          hidden
          type="file"
          accept="image/*"
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = '';
            if (f) onFile(f);
          }}
        />
      </Button>
      <Button variant="outlined" disabled={status === 'processing'} onClick={() => setCamara(lado)}>
        {T.tomar}
      </Button>
    </>
  );

  /** El cartelito de estado, con los cuatro casos que el consolidador distingue. */
  const estadoDe = (
    verificacion: Verificacion | undefined,
    verificado?: boolean,
    confirmado?: boolean
  ): { texto: string; color: 'success' | 'info' | 'warning' | 'default'; lleno: boolean } => {
    if (verificacion === 'digito_de_control' || verificado)
      return { texto: T.verificado, color: 'success', lleno: true };
    if (verificacion === 'coincidencia_multiple')
      return { texto: T.coinciden, color: 'success', lleno: false };
    if (verificacion === 'en_conflicto')
      return { texto: T.enConflicto, color: 'warning', lleno: true };
    if (confirmado) return { texto: T.confirmado, color: 'info', lleno: true };
    return { texto: T.sinVerificar, color: 'default', lleno: false };
  };

  const fila = (
    etiqueta: string,
    valor?: string,
    verificado?: boolean,
    confirmado?: boolean,
    verificacion?: Verificacion,
    /** Una marca extra al lado del estado, para lo que no es verificación. */
    extra?: React.ReactNode,
    /**
     * Qué copiar al formulario. Son varios porque un mismo dato puede ir a más
     * de un lado: el número de documento se puede copiar como DNI o como el
     * CUIT que le corresponde, y quién elige es la persona.
     */
    copiar?: Array<{ etiqueta: string; hacer?: () => void }>
  ) =>
    valor ? (
      <TableRow key={etiqueta}>
        <TableCell sx={{ py: 0.5, pl: 0, width: '40%' }}>
          <Typography variant="body2" color="text.secondary">{etiqueta}</Typography>
        </TableCell>
        <TableCell sx={{ py: 0.5 }}>
          <Typography variant="body2" component="span" sx={{ mr: 1 }}>{valor}</Typography>
          {/* Una fila sin nada que verificar —un dato CALCULADO— no lleva
              cartelito: decir "sin verificar" de algo que no se leyó es
              contestar una pregunta que nadie hizo. */}
          {(verificacion || verificado !== undefined || confirmado !== undefined) &&
            (() => {
              const e = estadoDe(verificacion, verificado, confirmado);
              return (
                <Chip
                  size="small"
                  label={e.texto}
                  color={e.color}
                  variant={e.lleno ? 'filled' : 'outlined'}
                />
              );
            })()}
          {extra}
          {copiar
            ?.filter((c) => !!c.hacer)
            .map((c) => (
              <Button key={c.etiqueta} size="small" sx={{ ml: 1, minWidth: 0 }} onClick={c.hacer}>
                {c.etiqueta}
              </Button>
            ))}
        </TableCell>
      </TableRow>
    ) : null;

  const v = (campo: string) => !!datos?.verificados.includes(campo);

  return (
    <Dialog open={open} onClose={sending ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{T.title}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {T.intro}
        </Typography>
        {/* Que la foto traía las dos caras hay que DECIRLO: si no, los dos
            casilleros se llenan solos con una carga y parece un error. */}
        {propuesta && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>{T.cortePregunta}</Typography>
            {/* Los botones ANTES de la foto: una foto de las dos caras es alta,
                y abajo hay que ir a buscarlos con scroll. La pregunta se
                contesta mirando la línea, no leyendo toda la imagen. */}
            <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
              <Button size="small" variant="contained" onClick={aceptarCorte}>{T.corteSi}</Button>
              <Button size="small" onClick={rechazarCorte}>{T.corteNo}</Button>
            </Box>
            <canvas
              ref={propuestaRef}
              style={{ width: PREVIEW_WIDTH, maxWidth: '100%', borderRadius: 4 }}
            />
          </Alert>
        )}

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2">{T.anverso}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
            {botonesDeFoto('anverso', (f) => handleFoto('anverso', f), anversoStatus)}
            {anversoStatus === 'processing' && (
              <><CircularProgress size={18} /><Typography variant="body2">{T.procesando}</Typography></>
            )}
            {verdictAnverso?.ok && <Alert severity="success" sx={{ py: 0 }}>{T.okAnverso}</Alert>}
          </Box>
          {anversoStatus === 'error' && <Alert severity="error" sx={{ mt: 1 }}>{anversoError}</Alert>}
          {/* El aviso vale mientras las DOS caras sigan cargadas y sigan sin
              coincidir: si se vuelve a cargar una foto, el veredicto se
              recalcula y el cartel se va solo. */}
          {anversoStatus === 'read' &&
            reversoStatus !== 'processing' &&
            verdictAnverso &&
            !verdictAnverso.ok &&
            ACCIONABLES.includes(verdictAnverso.reason!) &&
            vieneAlCaso(verdictAnverso.reason) && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                {reasonMsg(verdictAnverso.reason, verdictAnverso.leido)}
              </Alert>
            )}
          {anversoStatus === 'read' && faltanDelFrente.length > 0 && renglonesAnverso.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary" component="div">
                {T.noSalieron} {faltanDelFrente.join(', ')}. {T.loQueVioElOcr}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                component="pre"
                sx={{ mt: 0.5, whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 11 }}
              >
                {renglonesAnverso.join('\n')}
              </Typography>
            </Box>
          )}
          {anversoStatus === 'read' &&
            (tapadoFrente.zonas.length > 0 || zonasSensiblesAnverso.length > 0) && (
            <Box sx={{ mt: 1 }}>
              {/* Solo si el número impreso quedó tapado de verdad, no con el código solo (KAN-452). */}
              {tramiteTapadoFrente && <Alert severity="success">{T.tramiteTapado}</Alert>}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={tapadoAutomatico}
                    onChange={(e) => setTapadoAutomatico(e.target.checked)}
                  />
                }
                label={T.taparAuto}
              />
              <Typography variant="caption" color="text.secondary" component="div">
                {T.taparAutoAyuda}
              </Typography>
            </Box>
          )}
          {(anversoStatus === 'read' || anversoStatus === 'error') && (
            <Box sx={{ mt: 1 }}>
              <canvas
                ref={previewAnversoRef}
                style={{
                  width: PREVIEW_WIDTH,
                  maxWidth: '100%',
                  borderRadius: 4,
                  cursor: anversoStatus === 'read' ? 'crosshair' : 'default',
                  touchAction: 'none',
                }}
                onPointerDown={anversoStatus === 'read' ? onPreviewDown : undefined}
                onPointerUp={anversoStatus === 'read' ? onPreviewUp : undefined}
              />
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                {marcas.length > 0
                  ? `${T.reconocido} ${marcas.map((r) => r.label).join(' · ')}`
                  : T.rNada}
              </Typography>
            </Box>
          )}
        </Box>

        <Box sx={{ mb: 1 }}>
          <Typography variant="subtitle2">{T.reverso}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
            {botonesDeFoto('reverso', (f) => handleFoto('reverso', f), reversoStatus)}
            {reversoStatus === 'processing' && (
              <><CircularProgress size={18} /><Typography variant="body2">{T.procesando}</Typography></>
            )}
            {verdictDorso?.ok && <Alert severity="success" sx={{ py: 0 }}>{T.okReverso}</Alert>}
          </Box>
          {reversoStatus === 'error' && <Alert severity="error" sx={{ mt: 1 }}>{reversoError}</Alert>}
          {reversoStatus === 'read' && tapadoDorso.garantizado && tapadoDorso.zonas.length > 0 && (
            <Alert severity="success" sx={{ mt: 1 }}>{T.tramiteTapado}</Alert>
          )}
          {(reversoStatus === 'read' || reversoStatus === 'error') && (
            <Box sx={{ mt: 1 }}>
              <canvas
                ref={previewReversoRef}
                style={{ width: PREVIEW_WIDTH, maxWidth: '100%', borderRadius: 4 }}
              />
            </Box>
          )}
        </Box>

        {extrayendo && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
            <CircularProgress size={18} />
            <Typography variant="body2">{T.extrayendo}</Typography>
          </Box>
        )}

        {datos && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2">{T.datosLeidos}</Typography>
            <Table size="small">
              <TableBody>
                {/* Estas tres filas muestran el ESTADO del consolidador, no un
                    booleano: con cuatro situaciones posibles, aplastarlas a
                    "verificado / sin verificar" tapaba el conflicto entre dos
                    fuentes, que es justo lo que hay que mirar en la foto. */}
                {fila(T.rApellido, textoDe('apellido', datos.lastName), v('lastName'),
                  undefined, camposTotales.apellido?.verificacion, undefined,
                  [{ etiqueta: T.copiar, hacer: copiarSiDifiere('apellido', author.lastName, (valor) => ({ lastName: valor })) }])}
                {fila(T.rNombre, textoDe('nombres', datos.firstName), v('firstName'),
                  undefined, camposTotales.nombres?.verificacion, undefined,
                  [{ etiqueta: T.copiar, hacer: copiarSiDifiere('nombres', author.firstName, (valor) => ({ firstName: valor })) }])}
                {fila(T.rNacimiento, textoDe('fechaNacimiento', datos.dateOfBirth), v('dateOfBirth'),
                  undefined, camposTotales.fechaNacimiento?.verificacion, undefined,
                  [{ etiqueta: T.copiar, hacer: copiarSiDifiere('fechaNacimiento', author.dateOfBirth, (valor) => ({ dateOfBirth: valor })) }])}
                {/* El sexo puede estar probado o no SEGÚN LA FUENTE: el código de
                    barras lo lleva con corrección de errores, el MRZ no —su
                    verificador compuesto saltea esa posición—. Estaba fijo en
                    "sin verificar", que era correcto cuando la única fuente era
                    el MRZ y dejó de serlo cuando empezamos a leer el código. */}
                {fila(
                  T.fGenero,
                  datos.gender ? GENERO_VISIBLE[datos.gender] : undefined,
                  v('gender'),
                  sexoConfirmado,
                  camposTotales.sexo?.verificacion,
                  undefined,
                  // El sexo del documento es "M" o "F"; el formulario guarda
                  // "male"/"female". Se compara y se copia lo del formulario,
                  // que es lo que el campo entiende.
                  [
                    {
                      etiqueta: T.copiar,
                      hacer:
                        onExtracted &&
                        estaVerificado('sexo') &&
                        datos.gender &&
                        datos.gender !== author.gender
                          ? () => onExtracted({ gender: datos.gender })
                          : undefined,
                    },
                  ]
                )}
                {/* El número va en su propia fila y SIEMPRE verificado: sale
                    del MRZ, con dígitos de control. Meterlo adentro del CUIT y
                    marcar todo junto como "sin verificar" tapaba justamente el
                    dato que sí está probado — lo que no lo está es el prefijo,
                    que se deduce del sexo. */}
                {/* El número puede venir del MRZ o del código de barras. Con
                    solo mirar el MRZ, un frente sin MRZ —el formato intermedio—
                    no mostraba el número aunque el código lo hubiera dado. */}
                {fila(
                  T.fDocumento,
                  camposTotales.numeroDocumento?.valor ??
                    (datos.taxIdType === 'du' ? datos.taxId : undefined),
                  v('taxId'),
                  undefined,
                  camposTotales.numeroDocumento?.verificacion,
                  undefined,
                  // Dos destinos para un mismo número, y los dos son el campo
                  // fiscal del formulario: el DNI tal cual, o el CUIT que le
                  // corresponde —que se calcula del número más el sexo, con su
                  // dígito verificador—. Copiar el DNI cambia también el TIPO
                  // del campo, porque un número sin su tipo no dice qué es.
                  //
                  // El CUIT se ofrece solo si se pudo calcular: sin el sexo del
                  // documento no hay prefijo, y un CUIT inventado en un campo
                  // fiscal es peor que ninguno.
                  [
                    {
                      etiqueta: T.copiarDni,
                      hacer: copiarSiDifiere('numeroDocumento', author.taxId, (valor) => ({
                        taxIdType: 'du',
                        taxId: valor,
                      })),
                    },
                  ]
                )}
                {/* El CUIT CALCULADO va en su propio renglón, con el número a la
                    vista: es un dato derivado —del documento más el sexo— y no
                    uno leído, así que esconderlo detrás de un botón en la fila
                    de otro campo obligaba a copiarlo para saber cuál era.

                    Se ofrece con dos condiciones: que se haya podido calcular, y
                    que el SEXO esté verificado. El prefijo sale del sexo, así
                    que con el sexo dudoso el número entero queda dudoso, y esto
                    va a un campo fiscal. El DNI se sigue pudiendo copiar. */}
                {estaVerificado('sexo') &&
                  fila(
                    T.fCuitCalculado,
                    cuitDelDocumento ?? undefined,
                    undefined,
                    undefined,
                    undefined,
                    <Chip size="small" label={T.calculado} sx={{ ml: 1 }} />,
                    [
                      {
                        etiqueta: T.copiar,
                        hacer: copiarSiDifiere(
                          'numeroDocumento',
                          author.taxId,
                          (valor) => ({ taxIdType: 'cuitCuil', taxId: valor }),
                          cuitDelDocumento
                        ),
                      },
                    ]
                  )}
                {/* Esta fila es la del CUIT/CUIL, y solo tiene sentido cuando el
                    documento aportó un CUIT. Cuando no lo aportó —un DNI sin
                    código de barras, por ejemplo—, `taxId` es el número de
                    documento otra vez: la fila salía con el mismo rótulo y el
                    mismo valor que la de arriba, y parecía un dato duplicado. */}
                {datos.taxIdType !== 'du' && fila(T.fFiscal, datos.taxId, v('taxId'))}
                {/* Estas dos filas salían del MRZ del DORSO, y en la tarjeta
                    2009-2012 el MRZ está en el FRENTE: con solo el frente
                    cargado desaparecían las dos, aunque estuvieran leídas. Van
                    contra los campos consolidados, que es lo que junta las dos
                    caras. */}
                {/* "Vencido" NO es un estado de verificación: la fecha puede
                    estar probada por su dígito de control Y ser pasada. Iban
                    pegados en el mismo texto y el documento vencido parecía un
                    dato mal leído. Van separados: el estado, y al lado la
                    marca roja. Vencido no impide enviar. */}
                {fila(
                  T.fVencimiento,
                  vencimiento ?? undefined,
                  undefined,
                  undefined,
                  camposTotales.fechaVencimiento?.verificacion,
                  vencido ? (
                    <Chip size="small" label={T.vencido} color="error" sx={{ ml: 1 }} />
                  ) : undefined
                )}
                {/* La nacionalidad no la cubre ningún dígito del MRZ, igual que
                    al sexo: solo sube de "lectura única" si el impreso dice lo
                    mismo. */}
                {fila(
                  T.fNacionalidad,
                  camposTotales.nacionalidad?.valor ?? undefined,
                  undefined,
                  nacionalidadConfirmada,
                  camposTotales.nacionalidad?.verificacion
                )}
                {/* Los campos del DNI de EXTRANJERO. Solo aparecen si están:
                    el documento argentino común no los tiene. */}
                {([
                  ['paisNacimiento', T.fPaisNacimiento],
                  ['fechaIngreso', T.fIngreso],
                  ['categoriaIngreso', T.fCategoria],
                  ['expediente', T.fExpediente],
                  ['disposicion', T.fDisposicion],
                  ['fechaRadicacion', T.fRadicacion],
                ] as [string, string][]).map(([campo, etiqueta]) =>
                  fila(etiqueta, textoDe(campo), undefined, undefined, camposTotales[campo]?.verificacion)
                )}
                {domicilio.province
                  ? [
                      fila(T.fCalle, domicilio.streetAndNumber, false),
                      fila(T.fLocalidad, domicilio.city, false),
                      fila(T.fProvincia, nombreDeFila('ProvinceState', domicilio.province), false),
                      fila(T.fPais, nombreDeFila('Country', domicilio.country), false),
                    ]
                  : fila(T.fDomicilio, datos.domicilioLeido || T.domicilioNoLeido, false)}
              </TableBody>
            </Table>
            {domicilio.province && (
              <Box sx={{ mt: 0.5 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={incluirDomicilio}
                      onChange={(e) => setIncluirDomicilio(e.target.checked)}
                    />
                  }
                  label={T.incluirDomicilio}
                />
                <Typography variant="caption" color="text.secondary" component="div">
                  {T.domicilioPuedeDiferir}
                </Typography>
              </Box>
            )}


            {vencido && (
              <Alert severity="info" sx={{ mt: 1 }}>{T.avisoVencido}</Alert>
            )}
            {revisionCuit.prefijoContradiceSexo && (
              <Alert severity="warning" sx={{ mt: 1 }}>{T.cuitSexo}</Alert>
            )}
            {revisionCuit.veredicto === 'coincide' && (
              <Alert severity="success" sx={{ mt: 1 }}>{T.cuitOk}</Alert>
            )}
            {/* Los dos carteles que PROPONÍAN un CUIT —"tiene cargado el DNI" y
                "el CUIT cargado no corresponde"— se fueron: la fila "CUIT/CUIL
                que le corresponde" dice lo mismo mejor, con el número a la
                vista y un botón para ponerlo. Dos lugares proponiendo el mismo
                dato es una invitación a que se contradigan. */}

            {onExtracted && (
              <Button variant="outlined" sx={{ mt: 1 }} onClick={completar}>
                {T.completar}
              </Button>
            )}
            {completado && <Alert severity="success" sx={{ mt: 1 }}>{T.completado}</Alert>}
          </Box>
        )}

        <CamaraDialog
          open={camara !== null}
          onClose={() => setCamara(null)}
          titulo={camara === 'reverso' ? T.camaraDorso : T.camaraFrente}
          cara={camara === 'reverso' ? 'dorso' : 'frente'}
          onCaptura={(foto, pista) => handleFoto(camara === 'reverso' ? 'reverso' : 'anverso', foto, pista)}
        />

        {avisoEnvio && !enviado && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            {avisoEnvio.faltan.length > 0 && (
              <Typography variant="body2">
                {T.avisoAlEnviar.replace('{faltan}', avisoEnvio.faltan.join(', '))}
              </Typography>
            )}
            {avisoEnvio.calidad.length > 0 && (
              <>
                <Typography variant="body2" sx={{ mt: avisoEnvio.faltan.length ? 1 : 0 }}>
                  {T.calidad}
                </Typography>
                <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                  {avisoEnvio.calidad.map((m) => (
                    <li key={m}>{m}</li>
                  ))}
                </ul>
              </>
            )}
            <Typography variant="body2" sx={{ mt: 1 }}>{T.calidadNoBloquea}</Typography>
          </Alert>
        )}
        {sendError && <Alert severity="error" sx={{ mt: 1 }}>{sendError}</Alert>}
        {enviado && (
          <Alert severity="success" sx={{ mt: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{T.enviado}</Typography>
            <Typography variant="body2">{T.enviadoDetalle}</Typography>
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          disabled={sending}
          variant={enviado ? 'contained' : 'text'}
        >
          {T.cerrar}
        </Button>
        <Button variant="contained" color="success" onClick={() => handleSend()} disabled={!canSend}>
          {sending ? <CircularProgress size={22} color="inherit" /> : T.enviar}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
